// src/features/expenses/services/expensesApi.ts
// FIXED WITH CORRECT ENDPOINTS AND CATEGORY SUPPORT

import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface Expense {
    id: string;
    description: string;
    amount: string;
    status: 'pending' | 'approved' | 'paid' | 'cancelled';
    categoryId: string;
    categoryName?: string;
    bankAccountId: string;
    bankAccountName?: string;
    branchId: string | null;
    branchName?: string;
    referenceNumber: string | null;
    paymentMethod: string | null;
    expenseDate: string;
    createdBy: string | null;
    createdAt: string;
    // ✅ New optional payee fields
    payeeBankName?: string;
    payeeAccountNumber?: string;
    payeeAccountName?: string;
}

export interface ExpenseCategory {
    id: string;
    name: string;
    description: string | null;
    isSystem?: boolean;
    isActive?: boolean;
    createdAt?: string;
}

export interface ExpenseListResponse {
    expenses: Expense[];
    totalCount: number;
}

// ============================================================
// EXPENSE CATEGORIES
// ============================================================

export async function getExpenseCategories(): Promise<ExpenseCategory[]> {
    const response = await apiClient.get('/expenses/categories');
    const data = response.data?.data || response.data;
    return Array.isArray(data) ? data : [];
}

export async function createExpenseCategory(data: {
    name: string;
    description?: string
}): Promise<ExpenseCategory> {
    const response = await apiClient.post('/expenses/categories', data);
    return response.data?.data || response.data;
}

// ============================================================
// EXPENSES
// ============================================================

export async function getExpenses(
    params: {
        limit?: number;
        page?: number;
        status?: string;
        categoryId?: string;
        branchId?: string;
        bankAccountId?: string;
        search?: string;
        sortBy?: string;
        order?: string;
    } = {}
): Promise<ExpenseListResponse> {
    const response = await apiClient.get('/expenses', { params });
    return {
        expenses: response.data?.data?.expenses || response.data?.data || [],
        totalCount: response.data?.data?.totalCount || response.data?.meta?.totalCount || 0,
    };
}

export async function getExpenseById(id: string): Promise<Expense> {
    const response = await apiClient.get(`/expenses/${id}`);
    return response.data?.data;
}

export async function createExpense(payload: {
    description: string;
    amount: number;
    categoryId: string;
    bankAccountId: string;
    branchId?: string;
    businessId?: string;
    paymentMethod?: string;
    referenceNumber?: string;
    notes?: string;
    expenseDate?: string;
    currency?: string;
    // ✅ New optional payee fields
    payeeBankName?: string;
    payeeAccountNumber?: string;
    payeeAccountName?: string;
}): Promise<Expense> {
    const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    const response = await apiClient.post('/expenses', cleanPayload);
    return response.data?.data;
}

export async function updateExpense(id: string, payload: Record<string, unknown>): Promise<Expense> {
    const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    const response = await apiClient.patch(`/expenses/${id}`, cleanPayload);
    return response.data?.data;
}

export async function approveExpense(id: string): Promise<Expense> {
    const response = await apiClient.post(`/expenses/${id}/approve`);
    return response.data?.data;
}

export async function cancelExpense(id: string): Promise<Expense> {
    const response = await apiClient.post(`/expenses/${id}/cancel`);
    return response.data?.data;
}

export async function deleteExpense(id: string): Promise<void> {
    await apiClient.delete(`/expenses/${id}`);
}